/** Shared STCW/MLC compliance examples for WRH rules UI and print report. */

export type ComplianceExampleCard = {
  title: string;
  schedule: string;
  ok: boolean;
  points: string[];
};

export function buildComplianceExamples(t: {
  minRest24h: number;
  maxWork24h: number;
  minRest7d: number;
  maxRestSplits: number;
  minLongestRestSegment: number;
  maxGapBetweenRest: number;
}): ComplianceExampleCard[] {
  const minR = t.minRest24h;
  const maxW = t.maxWork24h;
  const maxSplits = t.maxRestSplits;
  const minLong = t.minLongestRestSegment;
  const maxGap = t.maxGapBetweenRest;
  const min7 = t.minRest7d;

  return [
    {
      title: 'Compliant — classic 4-on / 8-off',
      schedule: 'Work 08:00–12:00 and 16:00–20:00 (repeat daily)',
      ok: true,
      points: [
        `Rest in any 24h ≈ 16h (above min ${minR}h). Overnight 20:00–08:00 counts as one continuous block.`,
        `Work in any 24h ≈ 8h (within max ${maxW}h).`,
        `Rest is in ${Math.min(2, maxSplits)} periods; longest overnight block ≈ 12h (≥ ${minLong}h).`,
        `Gap between rest periods = 4h watch (within max ${maxGap}h).`,
        `Weekly rest ≈ 112h if repeated 7 days (above min ${min7}h).`,
      ],
    },
    {
      title: 'Non-compliant — too much work',
      schedule: 'Work 06:00–21:00 (one long stretch)',
      ok: false,
      points: [
        `Work ≈ 15h in 24h → exceeds max work ${maxW}h.`,
        `Rest ≈ 9h → below min rest ${minR}h.`,
        'Month sheet flags Work 24h and Rest 24h; OT also rises if calendar work exceeds 8h.',
      ],
    },
    {
      title: 'Non-compliant — rest split into too many periods',
      schedule: 'Work 00:00–04:00, 08:00–12:00, and 16:00–20:00',
      ok: false,
      points: [
        `Three separate rest fragments in a sliding 24h window (max allowed: ${maxSplits}).`,
        'Even if total rest hours look fine, STCW limits how many times rest may be broken.',
        'Fix: merge watches or clear the shortest break so rest stays in at most two blocks.',
      ],
    },
    {
      title: 'Non-compliant — longest rest too short',
      schedule: 'Work 08:00–14:00 and 15:00–21:00',
      ok: false,
      points: [
        `Daytime rest 14:00–15:00 is only 1h; overnight pieces may still leave no block ≥ ${minLong}h.`,
        `Rule requires one continuous rest of at least ${minLong}h (usually the night rest).`,
        'Fix: protect a longer uninterrupted night block; avoid work that cuts it short.',
      ],
    },
  ];
}
